/**
 * GlobalDashboardPage - System-wide administrative dashboard
 * Displays global metrics for users, groups, and locks
 */

import { Page, View } from 'web-mojo';
import { MetricsChart, MetricsMiniChartWidget } from 'web-mojo/charts';

// Activity metrics mini charts
class ActivityMetricsView extends View {
  constructor(options = {}) {
    super({
      ...options,
      className: 'activity-metrics-section'
    });

    this.stats = {
      total_users: 0,
      total_groups: 0
    };
  }

  async getTemplate() {
    return `
      <div class="activity-metrics-header mb-4">
        <div class="row">
          <div class="col-xl-4 col-lg-4 col-12 mb-3">
            <div data-container="user_activity_day"></div>
          </div>

          <div class="col-xl-4 col-lg-4 col-12 mb-3">
            <div data-container="group_activity_day"></div>
          </div>

          <div class="col-xl-4 col-lg-4 col-12 mb-3">
            <div data-container="lock_activity_day"></div>
          </div>
        </div>
      </div>
    `;
  }

  async onInit() {
    let widgetConfig = {
      subtitle: '{{now_value}} <span class="subtitle-label">{{now_label}}</span> {{total}} <span class="subtitle-label">Month</span>',
      background: "#5388D6",
      textColor: "#FFFFFF",
      granularity: 'days',
      trendRange: 4,
      trendOffset: 0,
      chartType: 'bar',
      showTooltip: true,
      showXAxis: true,
      height: 50,
      chartWidth: '100%',
      fill: true,
      smoothing: 0.3,
      showTrending: true,
      color: 'rgba(245, 245, 255, 0.8)',
      fillColor: 'rgba(245, 245, 255, 0.6)'
    };

    this.userActivity = new MetricsMiniChartWidget({
      ...widgetConfig,
      icon: "bi bi-people fs-2",
      title: 'User Activity',
      subtitle: '{{now_value}} <span class="subtitle-label">{{now_label}}</span> {{total_users}} <span class="subtitle-label">Total</span>',
      slugs: ['user_activity_day'],
      account: 'global',
      background: "#5388D6",
      containerId: 'user_activity_day'
    });
    this.addChild(this.userActivity);

    this.groupActivity = new MetricsMiniChartWidget({
      ...widgetConfig,
      icon: "bi bi-collection fs-2",
      title: 'Group Activity',
      subtitle: '{{now_value}} <span class="subtitle-label">{{now_label}}</span> {{total_groups}} <span class="subtitle-label">Total</span>',
      slugs: ['group_activity_day'],
      account: 'global',
      background: "#1f6a7a",
      containerId: 'group_activity_day'
    });
    this.addChild(this.groupActivity);

    this.lockActivity = new MetricsMiniChartWidget({
      ...widgetConfig,
      icon: "bi bi-unlock-fill fs-2",
      title: 'Lock Activity',
      subtitle: '{{now_value}} <span class="subtitle-label">{{now_label}}</span>',
      slugs: ['lock_activity_day'],
      account: 'global',
      background: "#7CA59B",
      containerId: 'lock_activity_day'
    });
    this.addChild(this.lockActivity);
  }

  async loadValues() {
    try {
      const response = await this.getApp().rest.GET('/api/metrics/value/get', {
        slugs: ['total_users', 'total_groups'],
        account: "global"
      });

      if (response.success && response.data.status) {
        Object.assign(this.stats, response.data.data);
      }

      // Update widget headers with totals
      if (this.userActivity) {
        this.userActivity.header.total_users = this.stats.total_users || 0;
        this.userActivity.header.render();
      }

      if (this.groupActivity) {
        this.groupActivity.header.total_groups = this.stats.total_groups || 0;
        this.groupActivity.header.render();
      }
    } catch (error) {
      console.error('Failed to load system stats:', error);
    }
  }

  async onAfterRender() {
    await this.loadValues();
  }

  async refreshAll() {
    const promises = [
      this.loadValues(),
      this.userActivity?.refresh(),
      this.groupActivity?.refresh(),
      this.lockActivity?.refresh()
    ].filter(Boolean);

    await Promise.allSettled(promises);
  }
}

// Main Global Dashboard Page
export default class GlobalDashboardPage extends Page {
  constructor(options = {}) {
    super({
      ...options,
      title: 'Global Dashboard',
      icon: "bi-speedometer2",
      className: 'global-dashboard-page'
    });
  }

  async getTemplate() {
    return `
      <div class="dashboard-container container-lg">
        <!-- Page Header -->
        <div class="d-flex justify-content-end align-items-center mb-3">
          <div class="btn-group" role="group">
            <button type="button" class="btn btn-outline-secondary btn-sm"
                    data-action="refresh-all" title="Refresh All Charts">
              <i class="bi bi-arrow-clockwise"></i> Refresh
            </button>
          </div>
        </div>

        <!-- Activity Metrics -->
        <div data-container="activity-metrics"></div>

        <!-- Lock Operations Chart -->
        <div data-container="lock-operations-chart"></div>
      </div>
    `;
  }

  async onInit() {
    this.lastUpdated = new Date().toLocaleString();

    // Activity Metrics View
    this.activityMetricsView = new ActivityMetricsView({
      containerId: 'activity-metrics'
    });
    this.addChild(this.activityMetricsView);

    // Lock Operations Chart
    this.lockOperationsChart = new MetricsChart({
      title: '<i class="bi bi-unlock-fill"></i> Lock Operations',
      endpoint: '/api/metrics/fetch',
      height: 250,
      granularity: 'hours',
      slugs: ['lock_open'],
      account: 'global',
      chartType: 'bar',
      showDateRange: false,
      yAxis: {
        label: 'Opens',
        beginAtZero: true
      },
      tooltip: {
        y: 'number'
      },
      containerId: 'lock-operations-chart'
    });
    this.addChild(this.lockOperationsChart);
  }

  async onEnter() {
    await super.onEnter();
  }

  // Action Handlers
  async onActionRefreshAll(event, element) {
    try {
      // Show loading state
      if (element) {
        const icon = element.querySelector('i');
        icon?.classList.add('bi-spin');
        element.disabled = true;
      }

      // Refresh all components
      const promises = [
        this.activityMetricsView?.refreshAll(),
        this.lockOperationsChart?.refresh()
      ].filter(Boolean);

      await Promise.allSettled(promises);

      // Update last updated time
      this.lastUpdated = new Date().toLocaleString();

      // Emit refresh event
      const eventBus = this.getApp()?.events;
      if (eventBus) {
        eventBus.emit('global:dashboard-refreshed', {
          page: this,
          timestamp: this.lastUpdated
        });
      }

      console.log('Global dashboard refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh global dashboard:', error);
    } finally {
      // Reset button state
      if (element) {
        const icon = element.querySelector('i');
        icon?.classList.remove('bi-spin');
        element.disabled = false;
      }
    }
  }

  // Public API
  async refreshDashboard() {
    return this.onActionRefreshAll(null, null);
  }
}
